import { useDualSearch } from "@/shared/api/hooks/use-search";
import { type DualSearchResult } from "@/shared/api/search";
import { getStatusVariant } from "@/shared/lib/status";
import { Badge } from "@/shared/ui/badge";
import { Button } from "@/shared/ui/button";
import { Card, CardContent } from "@/shared/ui/card";
import { Input } from "@/shared/ui/input";
import { Hash, Search, X } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

const getRoutePath = (type: string): string => {
  switch (type) {
    case "account":
      return `/accounts`;
    case "application":
      return `/applications`;
    case "transaction":
      return `/transactions`;
    case "refund":
      return `/transactions`;
    default:
      return "/applications";
  }
};

const getTitle = (result: DualSearchResult): string => {
  return result.title || result.externalId || `Item ${result.id.slice(0, 8)}`;
};

const getSubtitle = (result: DualSearchResult): string => {
  return result.subtitle || "";
};

export function UniversalSearch() {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState<string | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(query.trim() || null);
    }, 300);

    return () => clearTimeout(timer);
  }, [query]);

  const { data: searchResults, isLoading: isSearching, error } = useDualSearch(debouncedQuery);

  const results = searchResults || [];

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-2 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search by ID"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                setDebouncedQuery(query.trim() || null);
              }
            }}
            className="h-11 bg-card pl-11 pr-10 focus-visible:ring-0 focus-visible:ring-offset-0"
          />
          {query && (
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-1 top-1/2 h-7 w-7 -translate-y-1/2 hover:bg-transparent"
              onClick={() => {
                setQuery("");
                setDebouncedQuery(null);
              }}
            >
              <X className="h-4 w-4 text-muted-foreground" />
            </Button>
          )}
        </div>
        <Button onClick={() => setDebouncedQuery(query.trim() || null)}>Search</Button>
      </div>

      {debouncedQuery &&
        (results.length > 0 ||
          isSearching ||
          (!isSearching && (error || results.length === 0))) && (
          <Card>
            <CardContent className="p-0">
              {isSearching ? (
                <div className="p-4 text-center text-muted-foreground">Searching...</div>
              ) : error || results.length === 0 ? (
                <div className="p-4 text-center text-muted-foreground">
                  No results found for "{debouncedQuery}"
                </div>
              ) : (
                <div className="divide-y">
                  {results
                    .filter((result) => result && result.id && result.type)
                    .map((result) => {
                      if (!result || !result.id || !result.type) return null;

                      const handleResultClick = () => {
                        if (!result?.id || !result?.type) return;
                        const path = getRoutePath(result.type);
                        const navigationId =
                          result.type === "refund" ? result.externalId : result.id;
                        navigate(`${path}?id=${encodeURIComponent(navigationId)}`, {
                          replace: true,
                        });
                        setQuery("");
                        setDebouncedQuery(null);
                      };

                      return (
                        <div
                          key={`${result.type}-${result.id}`}
                          className="cursor-pointer p-4 transition-colors hover:bg-muted"
                          onClick={handleResultClick}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <div className="mb-1 flex items-center gap-2">
                                <h4 className="font-medium">{getTitle(result)}</h4>
                                {result.type && (
                                  <Badge
                                    variant="outline"
                                    className="min-w-[80px] justify-center px-[6px] py-[1px] text-xs"
                                  >
                                    {result.type.charAt(0).toUpperCase() +
                                      result.type.slice(1).toLowerCase()}
                                  </Badge>
                                )}
                                {result.status && (
                                  <Badge
                                    variant={getStatusVariant(result.status)}
                                    className="min-w-[80px] justify-center px-[6px] py-[1px]"
                                  >
                                    {result.status.charAt(0).toUpperCase() +
                                      result.status.slice(1).toLowerCase()}
                                  </Badge>
                                )}
                              </div>
                              {getSubtitle(result) && (
                                <p className="mb-2 text-sm text-muted-foreground">
                                  {getSubtitle(result)}
                                </p>
                              )}
                              <div className="flex items-center gap-4 text-xs">
                                <div className="flex items-center gap-1">
                                  <Hash className="h-3 w-3" />
                                  <span className="text-muted-foreground">ID:</span>
                                  <code className="rounded bg-muted px-1 py-0.5">
                                    {result.externalId || result.id}
                                  </code>
                                </div>
                              </div>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleResultClick();
                              }}
                            >
                              View Details
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                </div>
              )}
            </CardContent>
          </Card>
        )}
    </div>
  );
}
